import React from 'react';
import { Translate } from 'react-redux-i18n';
import get from 'lodash.get';
import { BarChart3, TrendingUp, CircleDollarSign } from 'lucide-react';
import { ViewTable } from '../../components/layouts/viewTable';
import { ViewSectionRow } from '../../components/layouts/viewSectionRow';
import CurrencyHelper from '../../helpers/currencyHelper';
import { PageEntityModel } from './types';

interface PageEntityFundraisingDetailsProps {
  entity: PageEntityModel;
  organization?: any;
  translationBase: string;
}

export const PageEntityFundraisingDetails: React.FC<PageEntityFundraisingDetailsProps> = ({ entity, organization, translationBase }) => {
  const targetAmount = get(entity, 'targetAmount');
  const raisedAmount = get(entity, 'raisedAmount', 0);
  const donationCount = get(entity, 'donationCount', 0);
  const averageDonationAmount = get(entity, 'averageDonationAmount');
  const currency = get(entity, 'currency') || get(organization, 'currency');

  const numericTarget = typeof targetAmount === 'number' ? targetAmount : null;
  const numericRaised = typeof raisedAmount === 'number' ? raisedAmount : 0;
  const numericAvg = typeof averageDonationAmount === 'number' ? averageDonationAmount : null;

  const formattedRaisedAmount = CurrencyHelper.formatCurrency(numericRaised, currency);
  const formattedTargetAmount = numericTarget !== null ? CurrencyHelper.formatCurrency(numericTarget, currency) : null;
  
  const raisedVsTargetText = formattedTargetAmount 
    ? `${formattedRaisedAmount} / ${formattedTargetAmount}`
    : formattedRaisedAmount;
  
  const raisedVsTarget = (
    <div className="fundraising-metric-row">
      <BarChart3 size={18} />
      <span>{raisedVsTargetText}</span>
    </div>
  );
  
  const totalCount = (
    <div className="fundraising-metric-row">
      <TrendingUp size={18} />
      <span>{donationCount} donation{donationCount !== 1 ? 's' : ''}</span>
    </div>
  );
  
  const formattedAverageDonation = numericAvg !== null ? CurrencyHelper.formatCurrency(numericAvg, currency) : null;
  const averageDonationDisplay = formattedAverageDonation ? (
    <div className="fundraising-metric-row">
      <CircleDollarSign size={18} />
      <span>{formattedAverageDonation} avg donation</span>
    </div>
  ) : null;

  return (
    <div className="entity-fundraising-details">
      <ViewTable title={<Translate value={`${translationBase}.fundraising-details.title`} />}>
        <ViewSectionRow 
          title={`${translationBase}.fundraising-details.raised-vs-target`} 
          data={raisedVsTarget} 
          isShowEmptyFields={true} 
        />
        <ViewSectionRow 
          title={`${translationBase}.fundraising-details.total-count`} 
          data={totalCount} 
          isShowEmptyFields={true} 
        />
        {(donationCount ?? 0) > 0 && (
          <ViewSectionRow 
            title={`${translationBase}.fundraising-details.per-supporter`} 
            data={averageDonationDisplay} 
            isShowEmptyFields={true} 
          />
        )}
      </ViewTable>
    </div>
  );
};
